import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const PLAN_PRICES: Record<string, number> = {
  Starter: 299,
  Professional: 899,
  Enterprise: 1999,
  Trial: 0,
  Custom: 0,
};

export interface SATenant {
  id: string;
  name: string;
  slug: string;
  email: string;
  phone: string;
  city: string;
  country: string;
  brand_color: string;
  plan: string;
  status: string;
  trial_ends_at: string | null;
  created_at: string;
  user_count: number;
  bookings_mtd: number;
  mrr: number;
  owner_name: string;
  owner_email: string;
}

export interface SAUser {
  id: string;
  tenant_id: string | null;
  full_name: string;
  email: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  tenant_name: string;
}

export interface SAStats {
  total_agencies: number;
  total_users: number;
  total_mrr: number;
  trial_count: number;
  active_paid_count: number;
}

export function useSuperAdmin() {
  const [tenants, setTenants] = useState<SATenant[]>([]);
  const [users, setUsers] = useState<SAUser[]>([]);
  const [stats, setStats] = useState<SAStats>({
    total_agencies: 0,
    total_users: 0,
    total_mrr: 0,
    trial_count: 0,
    active_paid_count: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();

      const [tenantsRes, profilesRes, bookingsRes] = await Promise.all([
        supabase.from('tenants').select('*').order('created_at', { ascending: false }),
        supabase.from('profiles').select('id, tenant_id, full_name, email, role, is_active, last_login_at, created_at').order('created_at', { ascending: false }),
        supabase.from('bookings').select('tenant_id, id').gte('created_at', monthStart),
      ]);

      const rawTenants = tenantsRes.data || [];
      const allProfiles = profilesRes.data || [];
      const mtdBookings = bookingsRes.data || [];

      // Build lookup maps
      const userCountMap: Record<string, number> = {};
      const ownerMap: Record<string, { name: string; email: string }> = {};
      const tenantNameMap: Record<string, string> = {};

      rawTenants.forEach((t: any) => {
        tenantNameMap[t.id] = t.name;
      });

      allProfiles.forEach((p: any) => {
        if (p.tenant_id) {
          userCountMap[p.tenant_id] = (userCountMap[p.tenant_id] || 0) + 1;
          if (p.role === 'owner' || p.role === 'admin') {
            if (!ownerMap[p.tenant_id]) {
              ownerMap[p.tenant_id] = { name: p.full_name || 'Unknown', email: p.email || '' };
            }
          }
        }
      });

      const bookingCountMap: Record<string, number> = {};
      mtdBookings.forEach((b: any) => {
        if (b.tenant_id) bookingCountMap[b.tenant_id] = (bookingCountMap[b.tenant_id] || 0) + 1;
      });

      const enrichedTenants: SATenant[] = rawTenants.map((t: any) => {
        const owner = ownerMap[t.id];
        return {
          id: t.id,
          name: t.name || 'Unnamed Agency',
          slug: t.slug || '',
          email: t.email || '',
          phone: t.phone || '',
          city: t.city || '',
          country: t.country || '',
          brand_color: t.brand_color || '#6366F1',
          plan: t.plan || 'Trial',
          status: t.status || 'Trial',
          trial_ends_at: t.trial_ends_at || null,
          created_at: t.created_at,
          user_count: userCountMap[t.id] || 0,
          bookings_mtd: bookingCountMap[t.id] || 0,
          mrr: t.status === 'Active' ? (PLAN_PRICES[t.plan] || 0) : 0,
          owner_name: owner?.name || t.name?.split(' ')[0] || 'Agency Owner',
          owner_email: owner?.email || t.email || '',
        };
      });

      setTenants(enrichedTenants);

      const enrichedUsers: SAUser[] = allProfiles.map((u: any) => ({
        id: u.id,
        tenant_id: u.tenant_id || null,
        full_name: u.full_name || 'Unknown User',
        email: u.email || '',
        role: u.role || 'agent',
        is_active: u.is_active !== false,
        last_login_at: u.last_login_at || null,
        created_at: u.created_at,
        tenant_name: u.tenant_id ? (tenantNameMap[u.tenant_id] || 'Unknown Agency') : 'Platform',
      }));

      setUsers(enrichedUsers);

      const totalMRR = enrichedTenants.reduce((s, t) => s + t.mrr, 0);
      const trialCount = enrichedTenants.filter((t) => t.status === 'Trial').length;
      const activePaidCount = enrichedTenants.filter((t) => t.status === 'Active' && t.mrr > 0).length;

      setStats({
        total_agencies: enrichedTenants.length,
        total_users: enrichedUsers.filter((u) => u.role !== 'super_admin').length,
        total_mrr: totalMRR,
        trial_count: trialCount,
        active_paid_count: activePaidCount,
      });
    } catch (err) {
      console.error('SA data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { tenants, users, stats, loading, refetch: fetchData };
}
